import { courseOccurrences, type CourseOccurrence } from '../features/home/home-timeline';
import type { AcademicCourseDocument, AcademicTimetableDocument } from '../types/sync';

const HORIZON_DAYS = 200;
const MAX_TIMER_DELAY = 2_147_000_000;

export interface CourseReminderOccurrence extends CourseOccurrence {
  fireAt: Date;
  reminderMinutesBefore: number;
}

export function futureCourseReminders(
  courses: AcademicCourseDocument[],
  timetable: AcademicTimetableDocument | undefined,
  now = new Date(),
) {
  const end = new Date(now.getTime() + HORIZON_DAYS * 86_400_000);
  return courseOccurrences(courses, timetable, { start: now, end })
    .flatMap((occurrence): CourseReminderOccurrence[] => {
      if (!occurrence.schedule.reminderEnabled || !occurrence.startAt) return [];
      const reminderMinutesBefore = normalizeReminderLead(occurrence.schedule.reminderMinutesBefore);
      const fireAt = new Date(occurrence.startAt.getTime() - reminderMinutesBefore * 60_000);
      return fireAt > now ? [{ ...occurrence, fireAt, reminderMinutesBefore }] : [];
    })
    .sort((left, right) => left.fireAt.getTime() - right.fireAt.getTime() || left.id.localeCompare(right.id));
}

export function scheduleCourseReminders(
  courses: AcademicCourseDocument[],
  timetable: AcademicTimetableDocument | undefined,
  onDue: (occurrence: CourseReminderOccurrence) => void,
  now: () => number = Date.now,
) {
  const reminders = futureCourseReminders(courses, timetable, new Date(now()));
  const queues = new Map<string, CourseReminderOccurrence[]>();
  reminders.forEach(reminder => {
    const queue = queues.get(reminder.schedule.id) ?? [];
    queue.push(reminder);
    queues.set(reminder.schedule.id, queue);
  });
  const timers = new Set<ReturnType<typeof setTimeout>>();

  const arm = (queue: CourseReminderOccurrence[], index: number) => {
    const reminder = queue[index];
    if (!reminder) return;
    const remaining = reminder.fireAt.getTime() - now();
    if (remaining <= 0) {
      onDue(reminder);
      arm(queue, index + 1);
      return;
    }
    const timer = setTimeout(() => {
      timers.delete(timer);
      if (reminder.fireAt.getTime() <= now()) {
        onDue(reminder);
        arm(queue, index + 1);
      } else {
        arm(queue, index);
      }
    }, Math.min(remaining, MAX_TIMER_DELAY));
    timers.add(timer);
  };

  queues.forEach(queue => arm(queue, 0));
  return () => {
    timers.forEach(timer => clearTimeout(timer));
    timers.clear();
  };
}

export function normalizeReminderLead(value: number | undefined) {
  return [5, 10, 15, 30, 60].includes(value ?? 10) ? value ?? 10 : 10;
}
